import React, { Component } from "react";
import { MDBInput, MDBBtn, MDBIcon, MDBSpinner } from "mdb-react-ui-kit";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { MfmImageUploader } from "Projects/CC_cubi_clan_project/GlobalComponents/MfmImageUploader/MfmImageUploader";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import eventBus from "Projects/CC_cubi_clan_project/Utils/EventBus";
import RichTextEditor from "Projects/CC_cubi_clan_project/GlobalComponents/RichTextEditor/RichTextEditor";
import AdminUsersSection from "../AdminUsersSection/AdminUsersSection";
import IncidentWorkspace from "Projects/CC_cubi_clan_project/GlobalComponents/IncidentWorkspace/IncidentWorkspace";
import "./ProfilePage.css";

const SECTIONS = {
  ACCOUNT: "account",
  ACTOR: "Miembro",
  ADMIN_USERS: "admin_users",
  MY_INCIDENTS: "my_incidents",
};

export default class ProfilePage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSection: SECTIONS.ACCOUNT,
      loading: true,
      saving: false,

      // User data
      user: {
        IDUsuario: null,
        nombre: "",
        apellido: "",
        apodo: "",
        email: "",
        telefono: "",
        userImg: "",
        imageUuid: "",
      },
      showPasswordFields: false,
      newPassword: "",
      confirmPassword: "",

      // Actor data
      actor: null,
      actorLoaded: false,

      // Admin
      isAdmin: false,
    };
  }

  componentDidMount() {
    this.loadUserProfile();
    this.checkAdminPermission();
  }

  /* ── Data loading ── */

  loadUserProfile = async () => {
    this.setState({ loading: true });
    try {
      const response = await doFetch("SSO", "getMyProfile");
      const result = await response.json();
      if (result.STATE === "SUCCESS" && result.DATA) {
        const d = result.DATA;
        this.setState({
          user: {
            IDUsuario: d.iDUsuario ?? d.IDUsuario,
            nombre: d.nombre ?? "",
            apellido: d.apellido ?? "",
            apodo: d.apodo ?? "",
            email: d.email ?? "",
            telefono: d.telefono ?? "",
            userImg: d.userImg ?? "",
            imageUuid: d.imageUuid ?? "",
          },
        });
      }
    } catch (err) {
      toast.error("Error al cargar el perfil");
    } finally {
      this.setState({ loading: false });
    }
  };

  checkAdminPermission = async () => {
    try {
      const response = await doFetch("MUGIN", "getMUGINUsers", { _silent: true });
      if (response.ok) {
        this.setState({ isAdmin: true });
      }
    } catch (_) {
      // Not admin or error — leave isAdmin false
    }
  };

  loadMyActor = async () => {
    if (this.state.actorLoaded) return;
    this.setState({ loading: true });
    try {
      const response = await doFetch("MUGIN", "getMyActor");
      const result = await response.json();
      if (result.STATE === "SUCCESS" && result.DATA) {
        this.setState({ actor: result.DATA, actorLoaded: true });
      } else {
        this.setState({ actor: null, actorLoaded: true });
      }
    } catch (err) {
      toast.error("Error al cargar datos de actor");
    } finally {
      this.setState({ loading: false });
    }
  };

  /* ── Section switching ── */

  switchSection = (section) => {
    this.setState({ activeSection: section }, () => {
      if (section === SECTIONS.ACTOR && !this.state.actorLoaded) {
        this.loadMyActor();
      }
    });
  };

  /* ── User field handlers ── */

  handleUserChange = (field, value) => {
    this.setState((prev) => ({
      user: { ...prev.user, [field]: value },
    }));
  };

  handleUserImageChange = (dataUrl) => {
    this.handleUserChange("userImg", dataUrl);
  };

  handleUserImageUuidChange = (result) => {
    if (result?.uuid) {
      this.handleUserChange("imageUuid", result.uuid);
    }
  };

  handleUserImageDelete = () => {
    this.handleUserChange("imageUuid", "");
  };

  /* ── Actor field handlers ── */

  handleActorChange = (field, value) => {
    this.setState((prev) => ({
      actor: { ...prev.actor, [field]: value },
    }));
  };

  handleActorImageUpload = (result) => {
    this.handleActorChange("imageUuid", result.uuid);
  };

  handleActorImageDelete = () => {
    this.handleActorChange("imageUuid", "");
  };

  /* ── Save user profile ── */

  saveUserProfile = async () => {
    const { user, newPassword, confirmPassword, showPasswordFields } =
      this.state;

    if (showPasswordFields && newPassword) {
      if (newPassword !== confirmPassword) {
        toast.error("Las contraseñas no coinciden");
        return;
      }
      if (newPassword.length < 4) {
        toast.error("La contraseña debe tener al menos 4 caracteres");
        return;
      }
    }

    this.setState({ saving: true });
    try {
      const payload = {
        nombre: user.nombre,
        apellido: user.apellido,
        email: user.email,
        telefono: user.telefono,
        userImg: user.userImg,
        imageUuid: user.imageUuid,
        password: showPasswordFields && newPassword ? newPassword : "",
      };

      const requestOptions = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      };

      const response = await doFetch("SSO", "updateMyProfile", requestOptions);
      const result = await response.json();

      if (result.STATE === "SUCCESS") {
        toast.success("Perfil actualizado correctamente");
        this.setState({
          showPasswordFields: false,
          newPassword: "",
          confirmPassword: "",
        });

        // Update sessionStorage and notify NavMenu
        const stored = JSON.parse(
          sessionStorage.getItem("userData") || "{}"
        );
        const updated = {
          ...stored,
          nombre: user.nombre,
          apellido: user.apellido,
          email: user.email,
          telefono: user.telefono,
          userImg: user.userImg,
          imageUuid: user.imageUuid,
        };
        sessionStorage.setItem("userData", JSON.stringify(updated));
        eventBus.emit("userUpdated", updated);
      } else {
        toast.error(result.MESSAGE || "Error al guardar");
      }
    } catch (err) {
      toast.error("Error al guardar el perfil");
    } finally {
      this.setState({ saving: false });
    }
  };

  /* ── Save actor profile ── */

  saveActorProfile = async () => {
    const { actor } = this.state;
    if (!actor) return;

    this.setState({ saving: true });
    try {
      const payload = {
        name: actor.name,
        bio: actor.bio,
        profileUrl: actor.profileUrl,
        imageUuid: actor.imageUuid,
      };

      const requestOptions = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      };

      const response = await doFetch(
        "MUGIN",
        "updateMyActor",
        requestOptions
      );
      const result = await response.json();

      if (result.STATE === "SUCCESS") {
        toast.success("Datos de actor actualizados");
        if (result.DATA) {
          this.setState({ actor: result.DATA });
        }
      } else {
        toast.error(result.MESSAGE || "Error al guardar");
      }
    } catch (err) {
      toast.error("Error al guardar los datos de actor");
    } finally {
      this.setState({ saving: false });
    }
  };

  /* ── Render sections ── */

  renderAccountSection() {
    const { user, showPasswordFields, newPassword, confirmPassword, saving } =
      this.state;

    return (
      <div>
        <h2 className="pp-section-title">Mi cuenta</h2>

        {/* Profile image */}
        <div className="pp-image-section pp-account-image-section">
          <MfmImageUploader
            uuid={user.imageUuid}
            img={user.userImg}
            appCode="SSO"
            mediaType="image"
            mediaFormat="circle"
            mediaFrameSize="320x320"
            persistFraming={true}
            onUpload={this.handleUserImageUuidChange}
            onDelete={this.handleUserImageDelete}
          />
          <div className="pp-image-info">
            <h4>
              {user.nombre} {user.apellido}
            </h4>
            <p>@{user.apodo}</p>
          </div>
        </div>

        {/* Name fields */}
        <div className="pp-form-row">
          <MDBInput
            label="Nombre"
            value={user.nombre}
            onChange={(e) => this.handleUserChange("nombre", e.target.value)}
          />
          <MDBInput
            label="Apellido"
            value={user.apellido}
            onChange={(e) => this.handleUserChange("apellido", e.target.value)}
          />
        </div>

        {/* Username (read-only) */}
        <div className="pp-form-group">
          <MDBInput label="Usuario" value={user.apodo} disabled />
        </div>

        {/* Email & phone */}
        <div className="pp-form-row">
          <MDBInput
            label="Email"
            type="email"
            value={user.email}
            onChange={(e) => this.handleUserChange("email", e.target.value)}
          />
          <MDBInput
            label="Teléfono"
            value={user.telefono}
            onChange={(e) => this.handleUserChange("telefono", e.target.value)}
          />
        </div>

        {/* Password change */}
        <button
          className="pp-password-toggle"
          onClick={() =>
            this.setState((prev) => ({
              showPasswordFields: !prev.showPasswordFields,
              newPassword: "",
              confirmPassword: "",
            }))
          }
        >
          <MDBIcon fas icon={showPasswordFields ? "chevron-up" : "key"} />
          {showPasswordFields ? "Cancelar cambio" : "Cambiar contraseña"}
        </button>

        {showPasswordFields && (
          <div className="pp-password-fields">
            <MDBInput
              label="Nueva contraseña"
              type="password"
              value={newPassword}
              onChange={(e) => this.setState({ newPassword: e.target.value })}
            />
            <MDBInput
              label="Confirmar contraseña"
              type="password"
              value={confirmPassword}
              onChange={(e) =>
                this.setState({ confirmPassword: e.target.value })
              }
            />
          </div>
        )}

        {/* Save button */}
        <div className="pp-actions">
          <MDBBtn
            className="mugin-btn-primary"
            onClick={this.saveUserProfile}
            disabled={saving}
          >
            {saving ? (
              <MDBSpinner size="sm" className="me-2" />
            ) : (
              <MDBIcon fas icon="save" className="me-2" />
            )}
            Guardar
          </MDBBtn>
        </div>
      </div>
    );
  }

  renderActorSection() {
    const { actor, saving } = this.state;

    if (!actor) {
      return (
        <div>
          <h2 className="pp-section-title">Yo, Hapkidoin</h2>
          <div className="pp-no-actor">
            <MDBIcon fas icon="theater-masks" />
            <p>No tienes un perfil de actor vinculado a tu cuenta.</p>
          </div>
        </div>
      );
    }

    return (
      <div>
        <h2 className="pp-section-title">Yo, Hapkidoin</h2>

        {/* Actor image */}
        <div className="pp-image-section pp-actor-image-section">
          <MfmImageUploader
            uuid={actor.imageUuid}
            img={actor.imageBase64}
            appCode="MUGIN"
            mediaType="image"
            mediaFormat="3/4"
            mediaFrameSize="270x360"
            onUpload={this.handleActorImageUpload}
            onDelete={this.handleActorImageDelete}
          />
          <div className="pp-image-info">
            <h4>{actor.name}</h4>
            <p>Imagen del perfil de actor</p>
          </div>
        </div>

        {/* Actor name */}
        <div className="pp-form-group">
          <MDBInput
            label="Nombre artístico"
            value={actor.name || ""}
            onChange={(e) => this.handleActorChange("name", e.target.value)}
          />
        </div>

        {/* Profile URL */}
        <div className="pp-form-group">
          <MDBInput
            label="URL del perfil"
            value={actor.profileUrl || ""}
            onChange={(e) =>
              this.handleActorChange("profileUrl", e.target.value)
            }
          />
        </div>

        {/* Bio */}
        <div className="pp-bio-editor">
          <div className="pp-bio-label">Biografía</div>
          <RichTextEditor
            initialHtml={actor.bio || ""}
            onChangeHtml={(html) => this.handleActorChange("bio", html)}
            height={250}
          />
        </div>

        {/* Save button */}
        <div className="pp-actions">
          <MDBBtn
            className="mugin-btn-primary"
            onClick={this.saveActorProfile}
            disabled={saving}
          >
            {saving ? (
              <MDBSpinner size="sm" className="me-2" />
            ) : (
              <MDBIcon fas icon="save" className="me-2" />
            )}
            Guardar
          </MDBBtn>
        </div>
      </div>
    );
  }

  render() {
    const { activeSection, loading } = this.state;

    return (
      <div className="pp-container">
        {/* Sidebar */}
        <nav className="pp-sidebar">
          <button
            className={`pp-sidebar-item ${
              activeSection === SECTIONS.ACCOUNT ? "active" : ""
            }`}
            onClick={() => this.switchSection(SECTIONS.ACCOUNT)}
          >
            <MDBIcon fas icon="user-cog" />
            Mi cuenta
          </button>
          <button
            className={`pp-sidebar-item ${
              activeSection === SECTIONS.ACTOR ? "active" : ""
            }`}
            onClick={() => this.switchSection(SECTIONS.ACTOR)}
          >
            <MDBIcon fas icon="theater-masks" />
            Yo, Hapkidoin
          </button>
          {this.state.isAdmin && (
            <button
              className={`pp-sidebar-item ${
                activeSection === SECTIONS.ADMIN_USERS ? "active" : ""
              }`}
              onClick={() => this.switchSection(SECTIONS.ADMIN_USERS)}
            >
              <MDBIcon fas icon="users-cog" />
              Gestión de usuarios
            </button>
          )}
          <button
            className={`pp-sidebar-item ${
              activeSection === SECTIONS.MY_INCIDENTS ? "active" : ""
            }`}
            onClick={() => this.switchSection(SECTIONS.MY_INCIDENTS)}
          >
            <MDBIcon fas icon="life-ring" />
            Mis incidencias
          </button>
        </nav>

        {/* Content */}
        <main
          className={`pp-content${
            activeSection === SECTIONS.ADMIN_USERS
            || activeSection === SECTIONS.MY_INCIDENTS
              ? " pp-content-wide"
              : ""
          }`}
        >
          {loading ? (
            <div className="pp-loading">
              <MDBSpinner color="danger" />
            </div>
          ) : (
            <>
              {activeSection === SECTIONS.ACCOUNT &&
                this.renderAccountSection()}
              {activeSection === SECTIONS.ACTOR && this.renderActorSection()}
              {activeSection === SECTIONS.ADMIN_USERS && (
                <AdminUsersSection onLoading={this.props.onLoading} />
              )}
              {activeSection === SECTIONS.MY_INCIDENTS && (
                <IncidentWorkspace
                  appCode="MUGIN"
                  title="Incidencias MUGIN"
                  subtitle="Canal de soporte para usuarios de MUGIN"
                  initialTab="mine"
                  lockTab
                  enableManage={false}
                />
              )}
            </>
          )}
        </main>
      </div>
    );
  }
}
